import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ScheduleModal = ({ schedule, members, onClose, onSubmit }) => {
  const isEditing = !!schedule;
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    teamMemberId: '',
    assignmentTitle: '',
    description: '',
    serviceType: 'maintenance',
    clientName: '',
    clientPhone: '',
    location: '',
    startTime: '',
    endTime: '',
    priority: 'normal',
    requiredMaterials: '',
    status: 'pending',
  });

  useEffect(() => {
    if (schedule) {
      setFormData({
        teamMemberId: schedule?.teamMemberId || '',
        assignmentTitle: schedule?.assignmentTitle || '',
        description: schedule?.description || '',
        serviceType: schedule?.serviceType || 'maintenance',
        clientName: schedule?.clientName || '',
        clientPhone: schedule?.clientPhone || '',
        location: schedule?.location || '',
        startTime: schedule?.startTime ? new Date(schedule.startTime).toISOString().slice(0, 16) : '',
        endTime: schedule?.endTime ? new Date(schedule.endTime).toISOString().slice(0, 16) : '',
        priority: schedule?.priority || 'normal',
        requiredMaterials: schedule?.requiredMaterials?.join(', ') || '',
        status: schedule?.status || 'pending',
      });
    }
  }, [schedule]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      const submitData = {
        ...formData,
        requiredMaterials: formData.requiredMaterials
          ? formData.requiredMaterials.split(',').map(m => m.trim()).filter(Boolean)
          : [],
      };
      await onSubmit(submitData);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-xl shadow-premium border border-border max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-4 md:p-6 flex items-center justify-between">
          <h2 className="text-xl md:text-2xl font-bold text-foreground">
            {isEditing ? 'Modifier la Mission' : 'Nouvelle Mission'}
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <Icon name="X" className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 md:p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Membre d'équipe *</label>
            <select
              value={formData.teamMemberId}
              onChange={(e) => setFormData(prev => ({ ...prev, teamMemberId: e.target.value }))}
              className="w-full h-10 px-3 py-2 rounded-xl border border-slate-300 dark:border-white/10 bg-white dark:bg-slate-900/60 text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30"
              required
            >
              <option value="">Sélectionnez un membre</option>
              {members?.map(m => (
                <option key={m?.id} value={m?.id}>{m?.fullName} - {m?.role}</option>
              ))}
            </select>
          </div>

          <Input
            label="Titre de la mission *"
            value={formData.assignmentTitle}
            onChange={(e) => setFormData(prev => ({ ...prev, assignmentTitle: e.target.value }))}
            required
            placeholder="Ex: Maintenance climatisation"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Type de service</label>
              <select
                value={formData.serviceType}
                onChange={(e) => setFormData(prev => ({ ...prev, serviceType: e.target.value }))}
                className="w-full h-10 px-3 py-2 rounded-xl border border-slate-300 dark:border-white/10 bg-white dark:bg-slate-900/60 text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30"
              >
                <option value="maintenance">Maintenance</option>
                <option value="installation">Installation</option>
                <option value="transport">Transport</option>
                <option value="construction">Construction</option>
                <option value="it_network">IT & Réseaux</option>
                <option value="other">Autre</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Priorité</label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
                className="w-full h-10 px-3 py-2 rounded-xl border border-slate-300 dark:border-white/10 bg-white dark:bg-slate-900/60 text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30"
              >
                <option value="low">Faible</option>
                <option value="normal">Normale</option>
                <option value="high">Haute</option>
                <option value="urgent">Urgente</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Nom du client"
              value={formData.clientName}
              onChange={(e) => setFormData(prev => ({ ...prev, clientName: e.target.value }))}
              placeholder="Nom du client"
            />
            <Input
              label="Téléphone du client"
              value={formData.clientPhone}
              onChange={(e) => setFormData(prev => ({ ...prev, clientPhone: e.target.value }))}
              placeholder="+243..."
            />
          </div>

          <Input
            label="Localisation *"
            value={formData.location}
            onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
            required
            placeholder="Adresse de la mission"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Date/heure de début *"
              type="datetime-local"
              value={formData.startTime}
              onChange={(e) => setFormData(prev => ({ ...prev, startTime: e.target.value }))}
              required
            />
            <Input
              label="Date/heure de fin *"
              type="datetime-local"
              value={formData.endTime}
              onChange={(e) => setFormData(prev => ({ ...prev, endTime: e.target.value }))}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="w-full px-4 py-2 rounded-xl border border-slate-300 dark:border-white/10 bg-white dark:bg-slate-900/60 text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30"
              rows={3}
              placeholder="Détails de la mission..."
            />
          </div>

          <Input
            label="Matériels requis (séparés par des virgules)"
            value={formData.requiredMaterials}
            onChange={(e) => setFormData(prev => ({ ...prev, requiredMaterials: e.target.value }))}
            placeholder="Câbles, Outils, Peinture..."
          />

          {isEditing && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Statut</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                className="w-full h-10 px-3 py-2 rounded-xl border border-slate-300 dark:border-white/10 bg-white dark:bg-slate-900/60 text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30"
              >
                <option value="pending">En attente</option>
                <option value="assigned">Assigné</option>
                <option value="in_progress">En cours</option>
                <option value="completed">Terminé</option>
                <option value="cancelled">Annulé</option>
              </select>
            </div>
          )}

          <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t border-border">
            <Button type="button" variant="outline" onClick={onClose} className="w-full sm:w-auto">
              Annuler
            </Button>
            <Button type="submit" iconName="Save" iconPosition="left" className="w-full sm:w-auto" loading={loading}>
              {isEditing ? 'Modifier' : 'Créer'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ScheduleModal;
